import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  InputLabel,
  makeStyles,
  Select,
  Typography,
} from '@material-ui/core';

import { FieldType } from '..';
import { FieldWithOptionsResponse } from '../../../.generated/admin-api';
import { InputValidationResult } from '../..';
import { apiTranslate } from '../../../translations/translations';
import { OptionResponse } from '../../../.generated/admin-api/models/OptionResponse';

type OptionsFieldInputProps = {
  field: FieldType;
  required: boolean;
  value?: string;
  disabled?: boolean;
  onChangeValidator: (value: string) => InputValidationResult;
};

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    fontSize: '0.8rem',
  },
}));

interface Option {
  key: string;
  value: string;
}

const getAvailableOptions = (options?: Array<OptionResponse>): Option[] => {
  if (!options) {
    return [];
  }

  return options.map((option) => {
    return { key: option.key, value: apiTranslate(option.text) };
  });
};

const OptionsField: React.FC<OptionsFieldInputProps> = (props) => {
  const { field, required, value, disabled, onChangeValidator } = props;

  const classes = useStyles();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation(['translations']);

  const fieldWithOptions = field as FieldWithOptionsResponse;

  let title = apiTranslate(field.title);
  if (required) {
    title += '*';
  }

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;

    const validationResult = onChangeValidator(value);

    if (validationResult) {
      if (!validationResult.isValid) {
        const errorMessage =
          validationResult.errorMessage ??
          (validationResult.errorMessageKey
            ? t(
                validationResult.errorMessageKey,
                validationResult.errorMessageOptions,
              )
            : t('validation.default'));

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(undefined);
      }
    }
  };

  return (
    <>
      <FormControl fullWidth variant="outlined">
        <InputLabel>{title}</InputLabel>
        <Select
          native
          value={value ?? ''}
          onChange={handleChange}
          label={title}
          disabled={disabled}
        >
          <option aria-label="None" value="" />
          {getAvailableOptions(fieldWithOptions.options ?? undefined).map(
            (option) => (
              <option key={option.key} value={option.key}>
                {option.value}
              </option>
            ),
          )}
        </Select>
      </FormControl>
      <Typography className={classes.error}>
        {!!errorMessage && errorMessage}
      </Typography>
    </>
  );
};

export { OptionsField };
